/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import { createUseStyles } from 'react-jss';
import {
    SettingOutlined,
    SettingFilled,
    SmileOutlined,
    FrownOutlined,
    CloseOutlined,
} from '@ant-design/icons';
import {
    Divider,
    Checkbox,
    Switch,
    List,
    Timeline,
    Row,
    Col,
    Typography,
    Skeleton,
    Avatar,
} from 'antd';
import { isMobile } from 'react-device-detect';
import {
    collection, query, where, onSnapshot,
} from 'firebase/firestore';
import { firestore, auth } from '../../services/firebase';
import { signInWithCustomToken } from 'firebase/auth';
import InfiniteScroll from 'react-infinite-scroll-component';
import { NotificationDescription } from './NotificationComponent/Content';
import { CreateTitleForNotificationBody } from './NotificationComponent/Title';
import { ProfileAvatar } from './NotificationComponent/common';
import { notificationSettings, UserType } from './notificationConstants';
import { ListNotificationEnd } from '../assets/ListNotificationEnd';
import { updateUserNotificationPreference } from '../../services/api';
import {
    getNotificationData,
    getUserPreference,
    readBellNotification,
    readNotification,
} from './notificationAPIs';
import { getAuthValue } from '../../utils/authStorage';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

const useStyle = createUseStyles({
    panelContainer: {
        width: isMobile ? '100%' : '440px',
        maxWidth: '440px',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        margin: isMobile ? '0' : '0 auto',
    },
    panelHeader: {
        padding: '12px 16px',
        borderBottom: '1px solid #f0f0f0',
    },
    settingPage: {
        maxHeight: 'calc(100vh - 60px)',
        overflowY: 'auto',
        padding: '0 16px',
    },
    checkBoxStyle: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        paddingTop: '14px',
    },
    reciveTextStyle: {
        color: '#111',
        fontSize: '10px',
        fontWeight: 600,
    },
    notificationTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        color: '#111',
        fontSize: '16px',
        fontWeight: '600',
    },
    settingDivider: {
        '& .ant-divider': {
            margin: '18px 0px',
        },
    },
    showUnreadSwitch: {
        display: 'flex',
        flexDirection: 'row',
        gap: '8px',
    },
    avatarStyle: {
        '& .ant-list-item-meta-avatar': {
            marginRight: '10px',
        },
    },
    timeLine: {
        '&:hover': {
            backgroundColor: '#f0f0f0 !important',
            cursor: 'pointer',
        },
        '& .ant-timeline-item-tail': {
            top: '17px',
            left: '10px',
            borderLeft: '2px solid #DDD',
        },
        '& .ant-timeline-item-head-custom': {
            backgroundColor: 'transparent !important',
            top: '13px',
            left: '12px',
        },
        '& .ant-list-item': {
            paddingTop: '8px !important',
            paddingBottom: '11px !important',
        },
        paddingBottom: '0px',
    },
    timeLineHide: {
        '&:hover': {
            backgroundColor: '#f0f0f0 !important',
            cursor: 'pointer',
        },
        '& .ant-timeline-item-tail': {
            visibility: 'hidden',
        },
        '&  .ant-timeline-item-head-custom': {
            backgroundColor: 'transparent !important',
            top: '14px',
            left: '12px',
        },
        '& .ant-list-item': {
            paddingTop: '8px !important',
            paddingBottom: '4px !important',
        },
        paddingBottom: '0px',
    },
    notificationDivider: {
        marginTop: '6px',
        marginBottom: '6px',
    },
    moreNotification: {
        color: '#006EC3',
        fontWeight: 600,
        fontSize: '12px',
    },
    lessNotification: {
        color: '#006EC3',
        fontWeight: 600,
        fontSize: '12px',
        marginLeft: '28px',
        cursor: 'pointer',
    },
    markAllReadText: {
        color: '#006EC3',
        fontSize: '12px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    noAggreggateResultsContainer: {
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        alignItems: 'center',
    },
    noAggregateWrapper: {
        display: 'flex',
        flexDirection: 'column',
        fontSize: '1rem',
        gap: '1rem',
        fontWeight: 600,
        alignItems: 'center',
    },
    userName: {
        color: '#666',
        textAlign: 'right',
        fontSize: '12px',
        fontWeight: 400,
    },
    notificationsText: {
        color: '#111',
        fontSize: '16px',
        fontWeight: '600',
    },
    notificationEndStyle: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '5px',
    },
    notificationEndText: {
        display: 'flex',
        flexDirection: 'column',
    },
    markAllReadStyle: {
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
    },
    avatarTextStyle: {
        fontSize: '10px',
        display: 'flex',
        alignItems: 'center',
    },
    avatarGroupContainer: {
        '& .ant-avatar-group': {
            paddingTop: '2px',
        },
        display: 'flex',
        flexDirection: 'row',
        marginLeft: '28px',
        cursor: 'pointer',
        gap: '3px',
    },
    scrollableContent: {
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '0 10px',
    },
});

interface NotificationEntryProps {
    item: any;
    index: number;
    showMore: Record<string, boolean>;
    notificationData: any[];
    showMoreOnClick: (id: string, event: React.MouseEvent) => void;
    notificationHistoryLength: number;
    notificationStatus: Record<string, boolean>;
    setNotificationStatus: (status: Record<string, boolean>) => void;
    userId: string | null;
    organisationId: string | null;
}

const NotificationEntry = (props: NotificationEntryProps) => {
    const classes = useStyle();
    const { t } = useTranslation();
    const {
        item,
        index,
        showMore,
        notificationData,
        showMoreOnClick,
        notificationHistoryLength,
        notificationStatus,
        setNotificationStatus,
        userId,
        organisationId,
    } = props;

    // console.log('notificationData', notificationData);

    const onClickNotification = async (notificationHistoryId: string) => {
        if (!notificationStatus[notificationHistoryId]) {
            setNotificationStatus({
                ...notificationStatus,
                [notificationHistoryId]: true,
            });

            await readNotification({
                notificationHistoryId,
                notificationId: item.id,
                isRead: true,
                isReadAll: false,
                userId: userId || '',
                organisationId: organisationId || '',
            });
        }
        if (item.url) {
            if (item.sourceName === 'lia') {
                const currentOrigin = window.location.origin;
                const parsedUrl = new URL(item.url);
                const newUrl = `${currentOrigin}${parsedUrl.pathname}${parsedUrl.search}`;
                window.open(newUrl, '_blank');
            } else if (item.sourceName === 'support_ticket_notification') {
                const project_id = item.url;
                const issue_id = item.entityId;
                const issueUrl = `/ops/support-tickets/${organisationId}/projects/${project_id}/issues/${issue_id}/`;
                window.location.href = issueUrl;
            } else {
                window.open(item.url, '_blank');
            }
        }
    };

    const notificationHistory = item.notificationHistory;
    const uniqueUserIdsWithNames = Array.from(
        new Set(notificationHistory.slice(1).map((histItem: any) => histItem.actionUser?.userId)),
    ).map((uniqueUserId) => {
        const userObject = notificationHistory.find(
            (histItem: any) => histItem.actionUser?.userId === uniqueUserId,
        );
        return { uniqueUserId, userName: userObject.actionUser?.userName };
    });

    const moreThanTwoUniqueUserIds = uniqueUserIdsWithNames.length > 1;
    const avatarGroup: React.ReactNode[] = [];

    if (moreThanTwoUniqueUserIds) {
        uniqueUserIdsWithNames.forEach((data: any, idx: number) => {
            if (idx < 4) {
                // Find the history item for this user to get read status
                const historyItem = notificationHistory.find(
                    (h: any) => h.actionUser?.userId === data.uniqueUserId,
                );
                avatarGroup.push(
                    <ProfileAvatar
                        key={data.uniqueUserId || `avatar-${idx}`}
                        name={data?.userName}
                        sizeAvatar={19}
                        sizeText={10}
                        userType={item.userType}
                        isRead={historyItem ? notificationStatus[historyItem.id] : false}
                    />,
                );
            }
        });
    } else if (notificationHistoryLength > 1) {
        avatarGroup.push(
            <ProfileAvatar
                key="single"
                name={notificationHistory[1].actionUser?.userName}
                sizeAvatar={19}
                sizeText={10}
                userType={item.userType}
                isRead={notificationStatus[notificationHistory[1].id]}
            />,
        );
    }

    const AvatarGroup = ({ others }: { others: boolean }) => {
        const isWorker = item.userType === UserType.WORKER;
        return (
            <div
                className={classes.avatarGroupContainer}
                onClick={(e) => showMoreOnClick(item.id, e)}
            >
                <Avatar.Group>{avatarGroup}</Avatar.Group>
                <div className={classes.moreNotification}>
                    +
                    {notificationHistoryLength - 1}
                    {' '}
                    {isWorker ? (
                        t('updates')
                    ) : (
                        <>
                            {t('updates from')}
                            {' '}
                            {notificationHistory[1].actionUser?.userName}
                            {' '}
                            {others && t('and others')}
                        </>
                    )}
                </div>
            </div>
        );
    };

    return (
        <>
            <Timeline>
                <Timeline.Item
                    dot={(
                        <ProfileAvatar
                            name={item.notificationHistory[0].actionUser?.userName}
                            sizeAvatar={20}
                            sizeText={12}
                            userType={item.userType}
                            isRead={notificationStatus[item.notificationHistory[0].id]}
                        />
                    )}
                    className={
                        !showMore[item.id] ? classes.timeLineHide : classes.timeLine
                    }
                >
                    <List.Item>
                        <div
                            onClick={() => onClickNotification(item.notificationHistory[0].id)}
                            style={{ cursor: 'pointer', width: '100%' }}
                        >
                            <List.Item.Meta
                                className={classes.avatarStyle}
                                title={(
                                    <CreateTitleForNotificationBody
                                        item={item.notificationHistory[0]}
                                        ticketName={item.entityIdPrettyName}
                                        sourceName={item.sourceName}
                                        notificationId={item.id}
                                        notificationStatus={notificationStatus}
                                        setNotificationStatus={setNotificationStatus}
                                        userId={userId}
                                        organisationId={organisationId}
                                        userType={item.userType}
                                        entityTitle={item.entityTitle}
                                    />
                                )}
                                description={(
                                    <NotificationDescription
                                        item={item.notificationHistory[0]}
                                        title={item.entityTitle}
                                        notificationId={item.id}
                                        notificationStatus={notificationStatus}
                                        setNotificationStatus={setNotificationStatus}
                                        sourceName={item.sourceName}
                                        userId={userId}
                                        organisationId={organisationId}
                                        userType={item.userType}
                                        entityDescription={item.notificationHistory[0].entityDescription}
                                    />
                                )}
                            />
                        </div>
                    </List.Item>
                </Timeline.Item>
                {moreThanTwoUniqueUserIds ? (
                    <>{!showMore[item.id] && <AvatarGroup others />}</>
                ) : (
                    <>
                        {notificationHistoryLength > 1 && !showMore[item.id] && (
                            <AvatarGroup others={false} />
                        )}
                    </>
                )}
                {notificationHistoryLength > 1 && showMore[item.id] && (
                    <div>
                        <List
                            split={false}
                            itemLayout="horizontal"
                            dataSource={notificationData[index].notificationHistory}
                            renderItem={(histItem: any, i: number) => {
                                if (i === 0) return <></>;
                                return (
                                    <Timeline.Item
                                        key={histItem.id}
                                        dot={(
                                            <ProfileAvatar
                                                name={histItem.actionUser?.userName}
                                                sizeAvatar={20}
                                                sizeText={12}
                                                userType={notificationData[index].userType}
                                                isRead={notificationStatus[histItem.id]}
                                            />
                                        )}
                                        className={
                                            i !== notificationData[index].notificationHistory.length - 1
                                                ? classes.timeLine
                                                : classes.timeLineHide
                                        }
                                    >
                                        <List.Item>
                                            <div
                                                onClick={() => onClickNotification(histItem.id)}
                                                style={{ cursor: 'pointer', width: '100%' }}
                                            >
                                                <List.Item.Meta
                                                    className={classes.avatarStyle}
                                                    title={(
                                                        <CreateTitleForNotificationBody
                                                            item={histItem}
                                                            notificationId={notificationData[index].id}
                                                            sourceName={notificationData[index].sourceName}
                                                            notificationStatus={notificationStatus}
                                                            setNotificationStatus={setNotificationStatus}
                                                            userId={userId}
                                                            organisationId={organisationId}
                                                            userType={notificationData[index].userType}
                                                            entityTitle={notificationData[index].entityTitle}
                                                        />
                                                    )}
                                                    description={(
                                                        <NotificationDescription
                                                            item={histItem}
                                                            notificationId={notificationData[index].id}
                                                            notificationStatus={notificationStatus}
                                                            setNotificationStatus={setNotificationStatus}
                                                            sourceName={notificationData[index].sourceName}
                                                            userId={userId}
                                                            organisationId={organisationId}
                                                            userType={notificationData[index].userType}
                                                            entityDescription={histItem.entityDescription}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </List.Item>
                                    </Timeline.Item>
                                );
                            }}
                        />
                        <div
                            onClick={(event) => showMoreOnClick(item.id, event)}
                            className={classes.lessNotification}
                        >
                            {' '}
                            {t('Show Less')}
                        </div>
                    </div>
                )}
            </Timeline>
            {index !== notificationData.length - 1 && (
                <Divider className={classes.notificationDivider} />
            )}
        </>
    );
};

const NotificationPanel = () => {
    const { t } = useTranslation();
    const classes = useStyle();
    const [setting, setSetting] = useState<boolean>(false);
    const [showUnreadNotification, setShowUnreadNotification] = useState<boolean>(true);
    const [checkedList, setCheckedList] = useState<string[]>([
        'lia',
        'support_ticket_notification',
    ]);
    const [showMore, setShowMore] = useState<Record<string, boolean>>({});
    const [notificationStatus, setNotificationStatus] = useState<Record<string, boolean>>({});
    const [notificationData, setNotificationData] = useState<any[]>([]);
    const [errorInNotification, setErrorInNotification] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [loaderSetting, setLoaderSetting] = useState(false);

    // Use localStorage-based auth helpers
    const authToken = getAuthValue('firebaseAuthToken');
    const [userId, setUserId] = useState<string | null>(getAuthValue('userId') || null);
    const [organisationId, setOrganisationId] = useState<string | null>(getAuthValue('organisationId') || null);

    const CheckboxGroup = Checkbox.Group;

    const getAllNotificationData = async () => {
        if (!userId || !organisationId) return;

        const response = await getNotificationData({
            userId,
            organisationId,
            authToken,
        });

        if (!response) {
            setErrorInNotification(true);
            setHasMore(false);
        }

        if (JSON.stringify(response) !== JSON.stringify(notificationData)) {
            setNotificationData(response ?? []);
            if (response?.length < 10) {
                setHasMore(false);
            } else if (response?.length >= 10) {
                setHasMore(true);
            }
        }

        if (response?.length === 0) {
            setHasMore(false);
        }
        setLoaderSetting(false);
    };

    useEffect(() => {
        let unsubscribe = () => {};

        const currentUserType = localStorage.getItem('userType');
        const fetchAndSetAuthIds = async () => {
            // Use config values instead of API call
            const currentUserId = localStorage.getItem('userId');
            const currentOrgId = localStorage.getItem('organisationId');

            setUserId(currentUserId);
            setOrganisationId(currentOrgId);

            if (currentUserId && currentOrgId && authToken && auth && firestore) {
                signInWithCustomToken(auth, authToken)
                    .then(() => {
                        const userNotificationCountCollection = collection(
                            firestore!,
                            'userNotificationCount',
                        );
                        const userNotificationsQuery = query(
                            userNotificationCountCollection,
                            where('userId', '==', currentUserId),
                            where('organisationId', '==', currentOrgId),
                            where('userType', '==', currentUserType),
                        );
                        unsubscribe = onSnapshot(userNotificationsQuery, (snapshot) => {
                            snapshot.forEach((doc) => {
                                // Notification count tracked for real-time updates
                                // eslint-disable-next-line no-console
                                console.log('Unread count:', doc.data().count);
                            });
                        });
                    })
                    .catch((err) => {
                        console.log('error', err);
                        setErrorInNotification(true);
                        setLoaderSetting(false);
                        setHasMore(false);
                    });
            }
        };

        const getInitialNotificationUserPreference = async () => {
            // Skip user preference check for worker userType
            if (currentUserType === UserType.WORKER) {
                setCheckedList(['project_x']);
                setShowUnreadNotification(false);
                return;
            }

            try {
                if (userId && organisationId) {
                    const response = await getUserPreference({
                        userId,
                        organisationId,
                    });
                    if (Object.keys(response ?? {}).length === 0) {
                        setCheckedList(['lia']);
                        setShowUnreadNotification(true);
                    } else {
                        setCheckedList(response?.allowedServiceNotifications);
                        setShowUnreadNotification(response?.showOnlyUnread);
                    }
                }
            } catch (error) {
                console.log('error', error);
            }
        };

        fetchAndSetAuthIds();
        getInitialNotificationUserPreference();

        // Mark bell as read and fetch notifications on mount
        if (userId && organisationId) {
            readBellNotification({ userId, organisationId });
            getAllNotificationData();
        }

        return () => {
            unsubscribe();
        };
    }, [authToken, userId, organisationId]);

    useEffect(() => {
        const initialObject: Record<string, boolean> = {};
        if (!notificationData) return;
        if (notificationData && notificationData.length === 0) return;

        const resultObject = notificationData.reduce((acc, notification) => {
            acc[notification.id] = false;
            return acc;
        }, initialObject);
        setShowMore(resultObject);

        const newNotificationStatus: Record<string, boolean> = {};
        notificationData.forEach((notification) => {
            notification.notificationHistory.forEach((historyItem: any) => {
                newNotificationStatus[historyItem.id] = true;
                if (
                    notification.unreadIds
          && notification.unreadIds.includes(historyItem.id)
                ) {
                    newNotificationStatus[historyItem.id] = false;
                }
            });
        });
        setNotificationStatus(newNotificationStatus);
    }, [notificationData]);

    const onChangeCheckedList = async (list: any[]) => {
        setLoaderSetting(true);
        setNotificationData([]);
        setCheckedList(list as string[]);
        await updateUserNotificationPreference({
            allowedServiceNotifications: list as string[],
            showOnlyUnread: showUnreadNotification,
        });
        getAllNotificationData();
    };

    const onChangeShowUnreadNotification = async (value: boolean) => {
        setLoaderSetting(true);
        setNotificationData([]);
        setShowUnreadNotification(value);
        await updateUserNotificationPreference({
            allowedServiceNotifications: checkedList,
            showOnlyUnread: value,
        });
        getAllNotificationData();
    };

    const showMoreOnClick = (id: string, event: React.MouseEvent) => {
        event.stopPropagation();
        const newShowMore = { ...showMore };
        newShowMore[id] = !showMore[id];
        setShowMore(newShowMore);
    };

    const markAllasReadClick = async () => {
        const newNotificationStatus = { ...notificationStatus };
        Object.keys(newNotificationStatus).forEach((key) => {
            newNotificationStatus[key] = true;
        });
        setNotificationStatus(newNotificationStatus);
        if (userId && organisationId) {
            await readNotification({ isReadAll: true, userId, organisationId });
        }
    };

    const loadMoreData = async () => {
        if (!userId || !organisationId) return;

        const response = await getNotificationData({
            userId,
            organisationId,
            lastNotificationId: notificationData[notificationData.length - 1].id,
            authToken,
        });
        setNotificationData([...notificationData, ...response]);
        if (response.length === 0 || response.length < 10) {
            setHasMore(false);
        }
    };

    const EmptyIcon = errorInNotification ? FrownOutlined : SmileOutlined;

    const SettingIcon = setting ? SettingFilled : SettingOutlined;

    const onSettingIconClick = () => {
        setSetting(!setting);
    };

    const notificationHeader = (
        <div className={classes.notificationTitle}>
            <div className={classes.notificationsText}>{t('Notifications')}</div>
            <div className={classes.markAllReadStyle}>
                {notificationData.length > 0 && (
                    <div
                        className={classes.markAllReadText}
                        onClick={() => markAllasReadClick()}
                    >
                        {t('Mark all as read')}
                    </div>
                )}
                {!errorInNotification && localStorage.getItem('userType') !== UserType.WORKER && (
                    <SettingIcon
                        onClick={onSettingIconClick}
                        style={{ fontSize: '15px', cursor: 'pointer' }}
                    />
                )}
                {!errorInNotification && localStorage.getItem('userType') === UserType.WORKER && (
                    <CloseOutlined
                        onClick={() => (window as any).webkit?.messageHandlers?.webClientListener?.postMessage(
                            'main_page_back_press',
                        )}
                        style={{ fontSize: '15px', cursor: 'pointer' }}
                    />
                )}
            </div>
        </div>
    );

    const settingsContent = (
        <div className={classes.settingPage}>
            <div className={classes.reciveTextStyle}>
                {t('Receive Notifications from')}
                ...
            </div>
            <CheckboxGroup
                options={notificationSettings}
                value={checkedList}
                onChange={(ea) => onChangeCheckedList(ea)}
                className={classes.checkBoxStyle}
            />
            <Divider className={classes.settingDivider} />
            <div className={classes.showUnreadSwitch}>
                <Switch
                    size="small"
                    checked={showUnreadNotification}
                    onChange={(e) => onChangeShowUnreadNotification(e)}
                />
                <div>
                    {t('Show only Unread Notifications')}
                    {' '}
                </div>
            </div>
        </div>
    );

    const notificationListContent = (
        <div
            id="scrollableDiv"
            className={classes.scrollableContent}
        >
            <InfiniteScroll
                dataLength={notificationData.length}
                next={loadMoreData}
                hasMore={hasMore}
                loader={<Skeleton avatar paragraph={{ rows: 1 }} active />}
                scrollableTarget="scrollableDiv"
                endMessage={(
                    <>
                        {notificationData.length > 10 && (
                            <div className={classes.notificationEndStyle}>
                                <ListNotificationEnd />
                                <div className={classes.notificationEndText}>
                                    <span>
                                        {' '}
                                        {t('Thats all your notifications from')}
                                    </span>
                                    <span style={{ textAlign: 'center' }}>
                                        {' '}
                                        {t('the last 30 days')}
                                        .
                                        {' '}
                                    </span>
                                </div>
                            </div>
                        )}
                    </>
                )}
            >
                <List
                    split={false}
                    itemLayout="horizontal"
                    dataSource={notificationData}
                    locale={{
                        emptyText: (
                            <>
                                {!hasMore && !loaderSetting && (
                                    <Row className={classes.noAggreggateResultsContainer}>
                                        <Col span={24} className={classes.noAggregateWrapper}>
                                            <EmptyIcon
                                                style={{
                                                    fontSize: '2rem',
                                                    color: errorInNotification ? '#FF2E2E' : '#21AF49',
                                                }}
                                            />
                                            <Text style={{ fontSize: '1rem' }}>
                                                {errorInNotification
                                                    ? t('Something Went Wrong')
                                                    : t('No New Notifications Available!')}
                                            </Text>
                                        </Col>
                                    </Row>
                                )}
                                {loaderSetting && (
                                    <Skeleton avatar paragraph={{ rows: 1 }} active />
                                )}
                            </>
                        ),
                    }}
                    renderItem={(item: any, index: number) => {
                        const notificationHistoryLength = notificationData[index].notificationHistory.length;
                        return (
                            <React.Fragment key={item.id}>
                                <NotificationEntry
                                    item={item}
                                    index={index}
                                    showMore={showMore}
                                    notificationHistoryLength={notificationHistoryLength}
                                    showMoreOnClick={showMoreOnClick}
                                    notificationData={notificationData}
                                    notificationStatus={notificationStatus}
                                    setNotificationStatus={setNotificationStatus}
                                    userId={userId}
                                    organisationId={organisationId}
                                />
                            </React.Fragment>
                        );
                    }}
                />
            </InfiniteScroll>
        </div>
    );

    return (
        <div className={classes.panelContainer}>
            <div className={classes.panelHeader}>
                {notificationHeader}
            </div>
            {setting ? settingsContent : notificationListContent}
        </div>
    );
};

export default NotificationPanel;
